import { formatLongDate, formatNumber } from './format'

/**
 * Dutch wording for the caveats the API carries as data.
 *
 * Three sections — `zoneAccess`, `financial.ownership` and `financial.roadTax` — ship
 * a `disclaimers` array. That is deliberate on the backend's part: the conditions attached to a
 * zero-emission transition or to the MRB overgangsregeling are the parts of Dutch law most likely
 * to move, and it carries them as data so a frontend developer does not have to track them. They
 * are not decoration and must not be summarised away.
 *
 * **They arrive in English.** The backend is written in English throughout — its enum members go on
 * the wire as `MilieuzoneGreen` and `NoTypeApproval` for the same reason — and these sentences were
 * written in the calculators alongside them. Every other piece of prose in a report is Dutch
 * because the RDW writes Dutch, so this is the first place the language of the API surfaces, and
 * English caveats on a Dutch page read as a fault in the page rather than as the legal warnings
 * they are.
 *
 * So they are translated here, matched on the sentence. That is a brittle key and it is chosen with
 * open eyes: the alternative is to drop them, and dropping a statement the API says must be shown
 * is worse than an occasional miss. The fallback is the English original rather than nothing —
 * a caveat that has not been translated yet is then visible on the page, which is how it gets
 * noticed and fixed, where a silent drop is how it does not.
 *
 * If the backend ever gives these entries codes instead of prose, this file becomes a lookup on the
 * code and the matching goes away. That would be the better contract; until then this is the honest
 * way to hold it.
 */

/**
 * The capture groups of a matched sentence.
 *
 * Typed as possibly absent because the compiler cannot know a group that the pattern requires did
 * in fact match. Every helper below therefore takes the absent case and falls back to the raw text
 * rather than printing "undefined" into a legal caveat.
 */
type Captures = (string | undefined)[]

interface Translation {
  /** The English sentence, with a capture group for every value interpolated into it. */
  english: RegExp
  dutch: (captures: Captures) => string
}

/** Reads a `yyyy-MM-dd` out of a disclaimer and writes it the way the rest of the page writes dates. */
function longDate(value: string | undefined): string {
  return (value === undefined ? undefined : formatLongDate(value)) ?? value ?? ''
}

/** Reads an invariant-culture `N0` figure — `13,000` — back into a number the page can format. */
function count(value: string | undefined): string {
  if (value === undefined) {
    return ''
  }

  const parsed = Number(value.replace(/,/g, ''))
  return Number.isNaN(parsed) ? value : (formatNumber(parsed) ?? value)
}

const TRANSLATIONS: readonly Translation[] = [
  // Zone access.
  {
    english: /^Worked out on (\d{4}-\d{2}-\d{2}) from the vehicle's registered emission class\.$/,
    dutch: ([date]) => `Bepaald op ${longDate(date)} aan de hand van de geregistreerde emissieklasse.`,
  },
  {
    english:
      /^Which municipalities operate which zone changes; this says whether the vehicle meets a zone's requirement, not where the zones are\.$/,
    dutch: () =>
      'Welke gemeente welke zone heeft, verandert. Dit zegt of dit voertuig aan de eis van een zone voldoet, niet waar die zones liggen.',
  },
  {
    english:
      /^Municipalities grant their own exemptions and day passes, and some run wider schemes than the national rules require\.$/,
    dutch: () =>
      'Gemeenten geven eigen ontheffingen en dagpassen, en sommige hanteren ruimere regels dan landelijk verplicht is.',
  },
  {
    english:
      /^A zero-emission zone transition ends on the date given, after which the vehicle may no longer enter\.$/,
    dutch: () =>
      'Een overgangsregeling voor een zero-emissiezone loopt af op de genoemde datum. Daarna mag het voertuig er niet meer in.',
  },

  // Road tax exemption and the transitional scheme.
  {
    english: /^Worked out on (\d{4}-\d{2}-\d{2}) from the date of first admission, at (\d+) years\.$/,
    dutch: ([date, years]) =>
      `Bepaald op ${longDate(date)} aan de hand van de datum eerste toelating, bij ${years ?? ''} jaar.`,
  },
  {
    english:
      /^Under the transitional scheme the charge is a quarter of the normal amount, up to an annual cap\.$/,
    dutch: () =>
      'Onder de overgangsregeling betaal je een kwart van het normale tarief, tot een jaarlijks maximum.',
  },
  {
    english:
      /^A vehicle on the transitional scheme may not be driven or parked on a public road in January, February or December\.$/,
    dutch: () =>
      'Een voertuig op de overgangsregeling mag in januari, februari en december niet op de openbare weg rijden of staan.',
  },
  {
    english:
      /^The transitional scheme requires the vehicle to run on petrol alone, and it closes as the exemption age advances past the fixed cut-off date\.$/,
    dutch: () =>
      'De overgangsregeling geldt alleen voor voertuigen die uitsluitend op benzine rijden, en loopt af naarmate de vrijstellingsleeftijd voorbij de vaste peildatum schuift.',
  },
  {
    english:
      /^Only vehicles first admitted before (\d{4}-\d{2}-\d{2}) become exempt with age; the ordinary road tax applies to this one at any age\.$/,
    dutch: ([date]) =>
      `Alleen voertuigen die vóór ${longDate(date)} voor het eerst zijn toegelaten, worden met de jaren vrijgesteld. Voor dit voertuig blijft het gewone tarief gelden, hoe oud het ook wordt.`,
  },
  {
    english: /^Until the exemption date the ordinary road tax applies\.$/,
    dutch: () => 'Tot de datum van vrijstelling geldt het gewone tarief van de motorrijtuigenbelasting.',
  },
  {
    english: /^An indication only\. The Belastingdienst's own assessment is the authoritative one\.$/,
    dutch: () =>
      'Dit is een indicatie. Voor de motorrijtuigenbelasting is de aanslag van de Belastingdienst bepalend.',
  },

  // Road tax amount.
  {
    english: /^Calculated on (\d{4}-\d{2}-\d{2}) using rates effective from (\d{4}-\d{2}-\d{2})\.$/,
    dutch: ([calculated, effective]) =>
      `Berekend op ${longDate(calculated)} met de tarieven die gelden vanaf ${longDate(effective)}.`,
  },
  {
    english:
      /^These are the (\d{4}) rates\. Rates and the provincial surcharge are set annually, so in a later year the amount will differ somewhat\.$/,
    dutch: ([year]) =>
      `Dit zijn de tarieven van ${year ?? ''}. De tarieven en de provinciale opcenten worden elk jaar opnieuw vastgesteld, dus in een later jaar wijkt het bedrag iets af.`,
  },
  {
    english: /^These are the (\d{4}) rates\. Rates are set annually, so in a later year the amount will differ somewhat\.$/,
    dutch: ([year]) =>
      `Dit zijn de tarieven van ${year ?? ''}. De tarieven worden elk jaar opnieuw vastgesteld, dus in een later jaar wijkt het bedrag iets af.`,
  },
  {
    english: /^A van pays no provincial surcharge, so the amount is the same in every province\.$/,
    dutch: () => 'Voor een bestelauto gelden geen provinciale opcenten, dus het bedrag is in elke provincie gelijk.',
  },
  {
    english:
      /^The rate depends on the keeper, which the RDW does not publish: an entrepreneur who uses the van more than incidentally in the business pays the business rate, anyone else the private rate\.$/,
    dutch: () =>
      'Welk tarief geldt, hangt af van de houder, en die maakt de RDW niet openbaar. Een ondernemer die de bestelauto meer dan bijkomstig voor zijn bedrijf gebruikt, betaalt het ondernemerstarief; iedere andere houder het tarief voor particulieren.',
  },
  {
    english: /^An electric or hydrogen van pays the full business rate; the law gives vans no reduction for it\.$/,
    dutch: () =>
      'Een elektrische bestelauto of een op waterstof betaalt het volle ondernemerstarief. De wet geeft bestelauto’s daarvoor geen korting.',
  },
  {
    english:
      /^No private rate is given for an electric or hydrogen van: the law does not settle clearly whether the passenger-car reduction applies to a van kept privately\.$/,
    dutch: () =>
      'Voor een particulier staat er geen bedrag bij. De wet maakt niet duidelijk of de korting voor elektrische personenauto’s ook geldt voor een bestelauto van een particulier.',
  },
  {
    english:
      /^The business rate is temporarily reduced for a tax period starting from (\d{4}-\d{2}-\d{2}) up to (\d{4}-\d{2}-\d{2})\. A period starting later pays the full business rate of (\d+) euros a quarter\.$/,
    dutch: ([from, until, full]) =>
      `Het ondernemerstarief is tijdelijk verlaagd voor een belastingtijdvak dat begint vanaf ${longDate(from)} en vóór ${longDate(until)}. Een tijdvak dat later begint, betaalt weer het volle ondernemerstarief van € ${full ?? ''} per kwartaal.`,
  },
  {
    english:
      /^No private rate is given: for this fuel the passenger-car rate depends on details the register does not publish\.$/,
    dutch: () =>
      'Voor een particulier staat er geen bedrag bij. Bij deze brandstof hangt het tarief af van gegevens die het kentekenregister niet openbaar maakt.',
  },
  {
    english: /^An indication only\. The Belastingdienst's own calculation is the authoritative one\.$/,
    dutch: () => 'Dit is een indicatie. De berekening van de Belastingdienst is bepalend.',
  },
  {
    english:
      /^The amount depends on the province the vehicle is kept in, which the RDW does not publish, so every province is listed\.$/,
    dutch: () =>
      'Het bedrag hangt af van de provincie waar de houder woont. Die maakt de RDW niet openbaar, daarom staat elke provincie erbij.',
  },
  {
    english: /^Assumes the keeper qualifies for no exemption or reduction\.$/,
    dutch: () => 'Gaat ervan uit dat de houder geen recht heeft op vrijstelling of korting.',
  },
  {
    english:
      /^No particulate emission is registered for this diesel, so the particulate surcharge the law presumes for a diesel first admitted before 1 September 2009 is included\.$/,
    dutch: () =>
      'Voor deze diesel is geen fijnstofuitstoot geregistreerd. Daarom is de fijnstoftoeslag meegerekend die de wet aanneemt voor een diesel die vóór 1 september 2009 voor het eerst is toegelaten.',
  },
  {
    english:
      /^A car driven only on electricity or hydrogen pays ([\d.]+)% of the full rate in (\d{4}); the percentage is set by law for each year\.$/,
    dutch: ([percent, year]) =>
      `Een auto die alleen op elektriciteit of waterstof rijdt, betaalt in ${year ?? ''} ${(percent ?? '').replace('.', ',')}% van het volle tarief. Dat percentage ligt per jaar vast in de wet.`,
  },

  // Energy cost.
  {
    english:
      /^Assumes ([\d,]+) km a year, which is a national average rather than this vehicle's own mileage\.$/,
    dutch: ([kilometres]) =>
      `Gaat uit van ${count(kilometres)} km per jaar. Dat is een landelijk gemiddelde en niet het kilometrage van deze auto.`,
  },
  {
    english: /^Uses the homologated consumption figure\. Real driving uses more\.$/,
    dutch: () => 'Gebaseerd op het verbruik uit de typegoedkeuring. In de praktijk ligt het verbruik hoger.',
  },
  {
    english: /^Fuel prices move week to week; the date of the price used is given alongside\.$/,
    dutch: () => 'Brandstofprijzen wisselen van week tot week. De datum van de gebruikte prijs staat erbij.',
  },
  {
    english: /^Electricity tariffs are set per month; the month of the tariff used is given alongside\.$/,
    dutch: () => 'Stroomtarieven worden per maand vastgesteld. De maand van het gebruikte tarief staat erbij.',
  },
  {
    english: /^A plug-in hybrid's real consumption depends entirely on how often it is charged\.$/,
    dutch: () => 'Bij een plug-in hybride hangt het werkelijke verbruik volledig af van hoe vaak er wordt opgeladen.',
  },
  {
    english:
      /^Electricity is priced at the average household tariff, as when charging at home\. Public charging costs considerably more\.$/,
    dutch: () =>
      'Stroom is gerekend tegen het gemiddelde tarief voor huishoudens, zoals bij thuisladen. Publiek laden is aanzienlijk duurder.',
  },
]

/**
 * Translates the API's caveats, keeping every one of them.
 *
 * An untranslated sentence is passed through as it came rather than dropped — see the note at the
 * top of this file for why that is the right way round.
 */
export function toDutch(disclaimers: readonly string[] | undefined): string[] {
  return (disclaimers ?? []).map((disclaimer) => {
    for (const translation of TRANSLATIONS) {
      const match = translation.english.exec(disclaimer)

      if (match) {
        return translation.dutch(match.slice(1))
      }
    }

    return disclaimer
  })
}
